import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { GraduationCap, Loader2, ShieldCheck } from 'lucide-react';
import EmailOtpForm from '@/components/EmailOtpForm';
import api from '@/lib/api';
import { routeByRole } from '@/utils/routeByRole';
import { setLastSchoolSlug } from '@/lib/schoolSession';
import { toast } from 'sonner';

const API = '/api';

/**
 * School Admin invite accept page — Ticket 1A.4.
 *
 * Flow:
 *   1. Fetch invite by token → show school branding + locked email
 *   2. Show EmailOtpForm with email pre-filled and locked
 *   3. On OTP verify success, call /api/invites/{token}/accept
 *   4. On accept success, route to /school-admin via next_route
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${token}`);
        if (!cancelled) {
          setInvite(data);
          // Cache the school slug so a 401 during the invite-accept flow
          // sends the user back to their school login (not the marketing page).
          if (data?.school?.slug) setLastSchoolSlug(data.school.slug);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Invite not found.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleVerified = async (userPayload) => {
    // After OTP verify, call accept endpoint. Session token from OTP verify is
    // already stored; api.js attaches it automatically.
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      const roleLabels = {
        teacher: 'teacher', school_admin: 'admin',
        student: 'student', parent: 'parent observer',
      };
      const roleLabel = roleLabels[data?.role] || 'member';
      toast.success(`Welcome! You're now a ${roleLabel} at ${invite?.school?.name || 'your school'}.`);
      const target = data?.next_route || '/';
      navigate(target, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Unable to accept invite.';
      toast.error(detail);
    }
    return false;
  };

  // --- Loading / error states ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading invite…</p>
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white px-4">
        <div className="text-center max-w-sm" data-testid="invite-error">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-3">
            {error || 'Invite unavailable'}
          </h2>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            This invite may be invalid or has already been used. Please ask your
            platform admin for a fresh invite.
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="invite-back-home"
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            ← Back to Yukti home
          </button>
        </div>
      </div>
    );
  }

  // Expired / already-consumed statuses — show school login as a primary path
  if (invite.status !== 'pending') {
    const labelMap = {
      accepted: 'This invite has already been accepted.',
      expired: 'This invite has expired.',
      revoked: 'This invite has been revoked.',
    };
    const school = invite.school;
    const brandColor = school?.brand_color || '#4F46E5';
    const schoolLoginHref = school?.slug ? `/school/${school.slug}/login` : null;
    const canGoToLogin = invite.status === 'accepted' && schoolLoginHref;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white px-4">
        <div className="text-center max-w-sm" data-testid="invite-consumed">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-3">
            {labelMap[invite.status] || 'Invite unavailable'}
          </h2>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            {canGoToLogin
              ? `You're all set up on ${school?.name || 'your school'}. Sign in below any time.`
              : 'Please contact support or ask for a new invite.'}
          </p>

          {schoolLoginHref && (
            <button
              onClick={() => navigate(schoolLoginHref)}
              className={`w-full rounded-xl py-3 font-medium text-white mb-3 ${
                canGoToLogin ? '' : 'opacity-90'
              }`}
              style={{ backgroundColor: brandColor }}
              data-testid="invite-goto-school-login"
            >
              Go to {school?.name || 'school'} login
            </button>
          )}

          <button
            onClick={() => navigate('/')}
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
            data-testid="invite-home-link"
          >
            ← Back to Yukti home
          </button>
        </div>
      </div>
    );
  }

  const { school } = invite;
  const brandColor = school?.brand_color || '#4F46E5';
  const bgUrl = school?.login_bg_url || null;
  const accentClass = 'text-white transition';

  const bgStyle = bgUrl
    ? {
        backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.72)), url(${bgUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <div
      className="adult-layout min-h-screen flex items-center justify-center px-4 py-10"
      style={bgStyle || { background: 'linear-gradient(to bottom, #eef2ff, #ffffff)' }}
      data-testid="invite-accept-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          {school?.logo_url ? (
            <img
              src={school.logo_url}
              alt={school.name}
              className="w-16 h-16 mx-auto mb-3 rounded-2xl object-contain bg-white shadow-md p-1"
              data-testid="invite-school-logo"
            />
          ) : (
            <div
              className="w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: brandColor }}
            >
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          )}
          <h1
            className={`text-2xl font-bold ${bgUrl ? 'text-white' : 'text-gray-900'}`}
            data-testid="invite-school-name"
          >
            {school?.name}
          </h1>
          <p className={`text-sm mt-1 ${bgUrl ? 'text-white/80' : 'text-gray-500'}`}>
            {invite.role === 'teacher'
              ? `You've been invited as Teacher${invite.subject ? ` · ${invite.subject.name}` : ''}`
              : invite.role === 'student'
              ? "You've been invited as a Student"
              : invite.role === 'parent'
              ? `You've been invited as a Parent observer${invite.name ? ` for ${invite.name}` : ''}`
              : "You've been invited as School Admin"}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-indigo-100">
          <p className="text-sm text-gray-600 mb-6">
            We'll email a 6-digit code to{' '}
            <span className="font-semibold text-gray-900">{invite.email}</span> to
            confirm it's you. No passwords needed.
          </p>

          <EmailOtpForm
            lockedEmail={invite.email}
            onVerified={handleVerified}
            accentClass={accentClass}
          />

          <style>{`[data-testid="email-otp-send-button"],[data-testid="email-otp-verify-button"]{background:${brandColor} !important;}[data-testid="email-otp-send-button"]:hover,[data-testid="email-otp-verify-button"]:hover{filter:brightness(0.92);}[data-testid="email-otp-resend-button"]{color:${brandColor} !important;}`}</style>
        </div>

        <p className={`text-center text-xs mt-6 ${bgUrl ? 'text-white/70' : 'text-gray-400'}`}>
          Not you?{' '}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="underline"
            style={{ color: bgUrl ? 'white' : brandColor }}
            data-testid="invite-home-link"
          >
            Go to Yukti home
          </button>
        </p>
      </motion.div>
    </div>
  );
}
